<template>
    <div class="flex flex-col gap-4">
        <h3 class="text-sm font-medium text-muval-gray-1">Files</h3>

        <p
            v-if="sourceDocument?.missing"
            class="text-sm text-muval-gray-2"
        >
            The original resource document has been deleted.
        </p>
        <MuvalButton
            v-else-if="sourceDocument"
            type="button"
            quinary
            @click="openSourceDocument"
            >Open original resource document</MuvalButton
        >
        <!--
            Attachments are damage photos, compliance certificates, service
            invoices. Served through short-lived signed URLs rather than public
            object URLs, so nothing here is a link that outlives the click.
        -->
        <FileDropZone
            v-if="canModify"
            @select="openFileDialog()"
            @drop="upload"
        />

        <MuvalLoader
            v-if="loading"
            class="mx-auto my-6"
            size="l"
        />

        <p
            v-else-if="!files.length && !pending.length"
            class="py-6 text-center text-sm text-muval-gray-2"
            data-test="company-diary-files-empty"
        >
            No files on this entry.
        </p>

        <TransitionGroup
            name="list"
            tag="div"
            class="flex flex-col gap-2 empty:hidden"
        >
            <PendingFileRow
                v-for="item in pending"
                :key="`pending-${item.id}`"
                :file="item"
                @retry="retry(item)"
                @dismiss="dismiss(item.id)"
            />

            <FileListRow
                v-for="file in files"
                :key="file.id"
                :file="file"
                :can-edit="canModify"
                :can-delete="canModify"
                @open="openFile(file)"
                @download="downloadFile(file)"
                @edit="openEditModal(file)"
                @delete="confirmDelete(file)"
            />
        </TransitionGroup>

        <FileDeleteModal
            v-model="isDeleteModalOpen"
            :file="fileToDelete"
            :loading="isDeleting"
            @confirm="removeFile"
        />

        <FileEditModal
            v-model="isEditModalOpen"
            :file="fileToEdit"
            :save="saveFile"
            @saved="handleEditSaved"
        />
    </div>
</template>

<script setup lang="ts">
import { useFileDialog } from '@vueuse/core';
import { toast } from 'vue-sonner';
import companyDiaryApi from '@/api/companyDiary';
import FileDeleteModal from '@components/files/FileDeleteModal.vue';
import FileDropZone from '@components/files/FileDropZone.vue';
import FileEditModal, { type FileEditValues } from '@components/files/FileEditModal.vue';
import FileListRow from '@components/files/FileListRow.vue';
import PendingFileRow from '@components/files/PendingFileRow.vue';
import MuvalLoader from '@components/loader/MuvalLoader.vue';
import { useVaporFileUploader } from '@composables/useVaporFileUploader';
import { getFileDisplayName, getFileExtension, type PendingFile } from '@utilities/fileDisplay';

/**
 * The entry's attachments (spec company-diary-entries, "Notes and file
 * attachments").
 *
 * Uploads go straight to S3 through a signed URL and are only then registered
 * against the entry, which is what keeps a large photo off the API's request
 * limit. Reads go the other way - a signed URL per click, never a stored public
 * one. Rows, upload target and dialogs are the shared `components/files` set, so
 * this list and the order Uploads tab cannot drift apart.
 */

const sourceDocument = ref<{ missing: boolean } | null>(null);
async function openSourceDocument() {
    try {
        const { data } = await window.tenantAxios.get(`/company-diary/entries/${props.entryId}/source-file`);
        window.open(data.download_url, '_blank', 'noopener');
    } catch {
        toast.error('You cannot open this resource document, or it is no longer available.');
    }
}

const props = defineProps<{
    /** Null while the entry is unsaved; files need something to hang off. */
    entryId: number | null;
    canModify: boolean;
}>();

const files = ref<any[]>([]);
const pending = ref<PendingFile[]>([]);
const loading = ref(false);
const isDeleteModalOpen = ref(false);
const isDeleting = ref(false);
const fileToDelete = ref<any>(null);
const isEditModalOpen = ref(false);
const fileToEdit = ref<any>(null);

let pendingId = 0;

const fetchFiles = async () => {
    if (!props.entryId) {
        files.value = [];

        return;
    }

    loading.value = true;
    try {
        const { data } = await companyDiaryApi.indexFiles(props.entryId);
        sourceDocument.value = data.source_document || null;
        files.value = data.data ?? [];
    } catch {
        toast.error('Failed to load files.');
    } finally {
        loading.value = false;
    }
};

watch(() => props.entryId, fetchFiles, { immediate: true });

const { open: openFileDialog, onChange } = useFileDialog({ multiple: true });

onChange((selected) => {
    if (selected) upload(Array.from(selected));
});

const dismiss = (id: string | number) => {
    pending.value = pending.value.filter((item) => item.id !== id);
};

/**
 * One pending row per file, so a failure names the file that failed rather than
 * the batch. The rows are removed on success - the uploaded file appears in the
 * list below in their place.
 */
const upload = async (selected: File[]) => {
    const entryId = props.entryId;

    if (!entryId || !selected.length) return;

    const rows: PendingFile[] = selected.map((file) => ({
        id: pendingId++,
        file,
        name: file.name,
        status: 'preparing',
        loaded: 0,
        total: file.size,
        percent: 0,
    }));

    pending.value = [...pending.value, ...rows];

    await send(rows, entryId);
};

/** Re-runs one failed row on its own, without the file being re-picked. */
const retry = (row: PendingFile) => {
    if (!props.entryId) return;

    row.status = 'preparing';
    row.loaded = 0;
    row.percent = 0;

    send([row], props.entryId);
};

const send = async (rows: PendingFile[], entryId: number) => {
    const selected = rows.map((row) => row.file);

    try {
        const {
            sign,
            store,
            upload: register,
        } = useVaporFileUploader({
            signUrl: '/company-diary/entries/vapor/signed-storage-url',
            bucket: import.meta.env.VITE_BUCKET_UPLOAD,
            scope: 'tenant',
            uploadUrl: `/company-diary/entries/${entryId}/files`,
        });

        const signed = await sign(selected);

        rows.forEach((row) => {
            row.status = 'uploading';
        });

        const stored = await store(selected, signed, ({ fileIndex, loaded, total }) => {
            const row = rows[fileIndex];

            if (!row) return;

            row.loaded = loaded;
            row.total = total || row.total;
            row.percent = row.total ? Math.round((loaded / row.total) * 100) : 0;
        });

        await register(stored, {});

        rows.forEach((row) => dismiss(row.id));
        toast.success(selected.length > 1 ? 'Files uploaded.' : 'File uploaded.');
        await fetchFiles();
    } catch (err: any) {
        rows.forEach((row) => {
            row.status = 'error';
        });
        toast.error(err?.message || 'Failed to upload the file.');
    }
};

const openFile = async (file: any) => {
    if (!props.entryId) return;

    try {
        const { data } = await companyDiaryApi.viewFile(props.entryId, file.id);
        window.open(data.download_url, '_blank', 'noopener');
    } catch {
        toast.error('Failed to open the file.');
    }
};

/**
 * Streamed through the API rather than opened from the signed URL, so the saved
 * file carries the label the operator gave it.
 */
const downloadFile = async (file: any) => {
    if (!props.entryId) return;

    try {
        const response = await companyDiaryApi.downloadFile(props.entryId, file.id);
        const mimeType = response.headers['content-type'] || file.mime_type || 'application/octet-stream';
        const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));

        const link = document.createElement('a');
        link.href = url;
        link.download = `${getFileDisplayName(file)}.${getFileExtension(file) || 'bin'}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
    } catch {
        toast.error('Failed to download the file.');
    }
};

const openEditModal = (file: any) => {
    fileToEdit.value = file;
    isEditModalOpen.value = true;
};

/**
 * No `shared_with_field_staff`: a diary entry has no order to share off, and its
 * chain carries a null `order_id` precisely so it cannot widen file access.
 */
const saveFile = (values: FileEditValues) =>
    companyDiaryApi.updateFile(props.entryId as number, fileToEdit.value.id, {
        display_label: values.display_label,
        tags: values.tags,
    });

const handleEditSaved = () => {
    fileToEdit.value = null;
    fetchFiles();
};

const confirmDelete = (file: any) => {
    fileToDelete.value = file;
    isDeleteModalOpen.value = true;
};

const removeFile = async () => {
    if (!props.entryId || !fileToDelete.value) return;

    isDeleting.value = true;
    try {
        await companyDiaryApi.destroyFile(props.entryId, fileToDelete.value.id);
        toast.success('File removed.');
        isDeleteModalOpen.value = false;
        fileToDelete.value = null;
        await fetchFiles();
    } catch {
        toast.error('Failed to remove the file.');
    } finally {
        isDeleting.value = false;
    }
};
</script>

<style scoped>
.list-move,
.list-enter-active,
.list-leave-active {
    transition:
        transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
        opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.list-enter-from {
    opacity: 0;
    transform: translateY(-8px);
}

.list-leave-to {
    opacity: 0;
    transform: translateX(20px) scale(0.98);
}

.list-leave-active {
    position: absolute;
    width: 100%;
}
</style>
